import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Logo from "../../assets/cellies.png";

// Shared settings for the public legal pages. Set REACT_APP_LEGAL_CONTACT_EMAIL
// to the address people should write to about privacy or these terms.
export const LEGAL = {
    orgName: "ANFGC Halifax",
    appName: "Cell Report",
    effectiveDate: "5 November 2026",
    contactEmail: process.env.REACT_APP_LEGAL_CONTACT_EMAIL || "",
};

export const ContactLine = () => (
    LEGAL.contactEmail ? (
        <>email <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a></>
    ) : (
        <>speak to your cell leader or a church administrator at {LEGAL.orgName}</>
    )
);

export const Section = ({ id, title, children }) => (
    <section id={id} className="scroll-mt-24">
        <h2 className="mt-10 text-xl font-semibold text-gray-900">{title}</h2>
        <div className="mt-3 space-y-3 text-[15px] leading-7 text-gray-700">{children}</div>
    </section>
);

Section.propTypes = { id: PropTypes.string.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node };
Section.defaultProps = { children: null };

export const LegalLinks = ({ className }) => (
    <nav aria-label="Legal" className={className}>
        <Link to="/privacy" className="hover:text-emerald-700 hover:underline">Privacy Policy</Link>
        <span aria-hidden="true" className="mx-2 text-gray-300">·</span>
        <Link to="/terms" className="hover:text-emerald-700 hover:underline">Terms of Service</Link>
    </nav>
);

LegalLinks.propTypes = { className: PropTypes.string };
LegalLinks.defaultProps = { className: "text-sm text-gray-500" };

const LegalLayout = ({ title, intro, sections, children }) => {
    useEffect(() => {
        document.title = `${title} · ${LEGAL.appName}`;
        return () => { document.title = LEGAL.appName; };
    }, [title]);

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="border-b border-gray-200 bg-white">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={Logo} alt="" className="h-9 w-9 rounded-lg object-contain" />
                        <span>
                            <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">{LEGAL.orgName}</span>
                            <span className="block text-base font-semibold text-gray-900">{LEGAL.appName}</span>
                        </span>
                    </Link>
                    <Link to="/login" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
                        Sign in
                    </Link>
                </div>
            </header>

            <main className="mx-auto grid max-w-5xl gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[14rem_1fr]">
                <aside className="hidden lg:block">
                    <nav aria-label="On this page" className="sticky top-8 text-sm">
                        <p className="font-semibold text-gray-900">On this page</p>
                        <ul className="mt-3 space-y-2 border-l border-gray-200">
                            {sections.map((s) => (
                                <li key={s.id}>
                                    <a href={`#${s.id}`} className="-ml-px block border-l border-transparent pl-3 text-gray-600 hover:border-emerald-500 hover:text-emerald-700">
                                        {s.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                <article className="legal-content rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-10">
                    <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
                    <p className="mt-2 text-sm text-gray-500">Effective {LEGAL.effectiveDate}</p>
                    <p className="mt-6 text-[15px] leading-7 text-gray-700">{intro}</p>
                    {children}
                </article>
            </main>

            <footer className="border-t border-gray-200 bg-white">
                <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-6 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <p>© {new Date().getFullYear()} {LEGAL.orgName}</p>
                    <LegalLinks />
                </div>
            </footer>
        </div>
    );
};

LegalLayout.propTypes = {
    title: PropTypes.string.isRequired,
    intro: PropTypes.node.isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, title: PropTypes.string })).isRequired,
    children: PropTypes.node,
};
LegalLayout.defaultProps = { children: null };

export default LegalLayout;
